import { useTheme } from "../hooks/use-theme";
import { LiftTheme } from "../theme";
import { StyleProp, StyleSheet, TextStyle, View, ViewStyle } from "react-native";
import React, { useMemo } from "react";
import Text from "../components/Text";

export type LabelMode =
    | "default"
    | "warning"
    | "error"
    | "success"
    | "info"
    | "secondary"
    | "cta"
    | "redCta"
    | "miniCta"
    | "miniCtaBold"
    | "anchor"
    | "medium"
    | "grey";

const LABEL_STYLES: Record<LabelMode, (theme: LiftTheme) => ViewStyle> = {
    cta: ({ colors, fonts }) => ({
        backgroundColor: "transparent",
        color: colors.secondary,
        borderColor: "transparent",
        paddingHorizontal: 0,
        paddingVertical: 0,
        ...fonts.bodyMedium,
    }),
    redCta: ({ colors, fonts }) => ({
        backgroundColor: "transparent",
        color: colors.notification,
        borderColor: "transparent",
        paddingHorizontal: 0,
        paddingVertical: 0,
        ...fonts.bodyMedium,
    }),
    miniCta: ({ colors, fonts }) => ({
        backgroundColor: "transparent",
        color: colors.secondary,
        borderColor: "transparent",
        paddingHorizontal: 0,
        paddingVertical: 0,
        ...fonts.bodySmall,
    }),
    miniCtaBold: ({ colors, fonts }) => ({
        backgroundColor: "transparent",
        color: colors.secondary,
        borderColor: "transparent",
        paddingHorizontal: 0,
        paddingVertical: 0,
        fontWeight: "bold",
        ...fonts.bodySmall,
    }),
    anchor: ({ colors, fonts }) => ({
        backgroundColor: "transparent",
        color: colors.primary,
        borderRadius: 0,
        paddingHorizontal: 0,
        paddingVertical: 0,
        ...fonts.bodyMedium,
    }),
    info: ({ colors }) => ({
        backgroundColor: colors.infoBackground,
        color: colors.onInfoBackground,
    }),
    secondary: ({ colors }) => ({
        backgroundColor: colors.onSecondaryContainer,
        color: colors.secondary,
    }),
    error: ({ colors }) => ({
        backgroundColor: colors.errorBackground,
        color: colors.onErrorBackground,
    }),
    success: ({ colors }) => ({
        backgroundColor: colors.successBackground,
        color: colors.onSuccessBackground,
    }),
    warning: ({ colors }) => ({
        backgroundColor: colors.warningBackground,
        color: colors.onWarningBackground,
    }),
    default: ({ colors }) => ({
        backgroundColor: colors.accentLighter,
        color: "#626262",
    }),
    medium: ({ colors, fonts }) => ({
        backgroundColor: "transparent",
        color: colors.text,
        borderColor: "transparent",
        borderWidth: 0,
        paddingHorizontal: 0,
        ...fonts.bodyMedium,
    }),
    grey: ({ colors }) => ({
        backgroundColor: colors.lighterGrey,
        color: colors.darkGrey,
    }),
};

type LabelProps = {
    text: string | number;
    mode?: LabelMode;
    style?: StyleProp<TextStyle>;
    multiline?: boolean;
};

const Label: React.FC<LabelProps> = ({ text, mode = "default", style, multiline }) => {
    const styles = useStyles();
    const theme = useTheme();
    return (
        <View style={styles.container}>
            <Text
                numberOfLines={multiline ? undefined : 1}
                style={[styles.labelText, LABEL_STYLES[mode](theme), style]}
            >
                {text}
            </Text>
            {mode == "anchor" && <View style={styles.underline} />}
        </View>
    );
};

const useStyles = () => {
    const { gridSize, fonts, colors } = useTheme();
    return useMemo(
        () =>
            StyleSheet.create({
                container: {
                    flexDirection: "column",
                },
                labelText: {
                    position: "relative",
                    justifyContent: "center",
                    verticalAlign: "middle",
                    borderRadius: 4,
                    overflow: "hidden",
                    paddingHorizontal: gridSize / 4,
                    paddingVertical: 1,
                    ...fonts.labelSmall,
                },
                underline: {
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 1,
                    backgroundColor: colors.primary,
                    zIndex: 2,
                },
            }),
        [fonts.labelSmall, gridSize, colors],
    );
};

export default Label;
